
import Vue from 'vue';
import type { PropOptions } from 'vue';
import { mapGetters, mapState } from 'vuex';
import PortfolioSpinner from '@/components/partials/public-portfolio/PortfolioSpinner.vue';
import PublicPortfolioPreviewHeader from '@/components/partials/public-portfolio/PublicPortfolioPreviewHeader.vue';
import PublicPortfolioEducationHistorySection from '@/components/partials/public-portfolio/PublicPortfolioEducationHistorySection/PublicPortfolioEducationHistorySection.vue';
import PublicPortfolioPage from '~/components/partials/public-portfolio/PublicPortfolioPage.vue';
import PublicPortfolioHero from '~/components/partials/public-portfolio/PublicPortfolioHero/PublicPortfolioHero.vue';
import PublicPortfolioClaim from '~/components/partials/public-portfolio/PublicPortfolioClaim/PublicPortfolioClaim.vue';
import PublicPortfolioClientsSection from '~/components/partials/public-portfolio/PublicPortfolioClients/PublicPortfolioClientsSection.vue';
import PublicPortfolioWorkHistorySection from '~/components/partials/public-portfolio/PublicPortfolioWorkHistory/PublicPortfolioWorkHistorySection.vue';
import PublicPortfolioSalesExperience from '~/components/partials/public-portfolio/PublicPortfolioSalesExperience/PublicPortfolioSalesExperience.vue';
import PublicPortfolioSection from '~/components/partials/public-portfolio/PublicPortfolioSection.vue';
import PublicPortfolioAwardsSection from '~/components/partials/public-portfolio/PublicPortfolioAwards/PublicPortfolioAwardsSection.vue';
import PublicPortfolioRecommendedUsersSection from '~/components/partials/public-portfolio/PublicPortfolioRecommendedUsers/PublicPortfolioRecommendedUsersSection.vue';
import PublicPortfolioTestimonialsSection from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/PublicPortfolioTestimonialsSection.vue';
import PublicPortfolioPostsSection from '~/components/partials/public-portfolio/PublicPortfolioPostsSection/PublicPortfolioPostsSection.vue';
import type { PublicPortfolioTestimonialMixinType } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/mixins/publicPortfolioTestimonialMixin';
import publicPortfolioTestimonialMixin from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/mixins/publicPortfolioTestimonialMixin';
import BravadoRightNavigation from '~/components/common/BravadoRightNavigation/BravadoRightNavigation.vue';
import type { Testimonial, PublicPortfolioUser, PublicPortfolioConfiguration, PostConnection } from '~/types/graphql';

type PortfolioSection = {
  key: string;
  title?: string;
  component: string;
  isVisible: boolean;
  canShowOnHeader: boolean;
  withSeparator?: boolean;
  props?: {
    testimonials?: Testimonial[];
    user?: PublicPortfolioUser | null;
    configuration?: PublicPortfolioConfiguration;
    posts?: PostConnection;
  };
};

export default (Vue as PublicPortfolioTestimonialMixinType).extend({
  name: 'PublicPortfolioPreview',
  components: {
    PublicPortfolioPage,
    PublicPortfolioHero,
    PublicPortfolioClaim,
    PublicPortfolioClientsSection,
    PublicPortfolioWorkHistorySection,
    PublicPortfolioSalesExperience,
    PublicPortfolioSection,
    PublicPortfolioAwardsSection,
    PublicPortfolioRecommendedUsersSection,
    PublicPortfolioTestimonialsSection,
    PortfolioSpinner,
    PublicPortfolioPreviewHeader,
    BravadoRightNavigation,
    PublicPortfolioEducationHistorySection,
    PublicPortfolioPostsSection,
  },
  mixins: [publicPortfolioTestimonialMixin],
  props: {
    publicPortfolioUser: {
      type: Object,
      required: true,
    } as PropOptions<PublicPortfolioUser>,
    selfPreview: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    publicPosts: {
      type: Object,
      required: true,
    } as PropOptions<PostConnection>,
  },
  computed: {
    ...mapState('portfolio', ['pending']),
    ...mapGetters('portfolio', [
      'isPortfolioEmpty',
      'hasClients',
      'hasDescription',
      'hasAwards',
      'hasSalesExperience',
      'hasWorkHistories',
    ]),
    configuration(): PublicPortfolioConfiguration {
      return this.publicPortfolioUser.configuration;
    },
    testimonials(): Testimonial[] {
      return this.publicPortfolioUser.testimonials || [];
    },
    isClientsSectionVisible(): boolean {
      return (
        (this.configuration.sectionClientsIsPublic && this.hasClients) ||
        (this.configuration.sectionHeadlineIsPublic && this.hasDescription)
      );
    },
    isClaimPlateVisible(): boolean {
      return !this.$auth.loggedIn && this.pending;
    },
    isTestimonialsSeparatorVisible(): boolean {
      return Boolean(this.testimonials?.length && !(this.configuration.sectionAwardsIsPublic && this.hasAwards));
    },
    portfolioSections(): PortfolioSection[] {
      return [
        {
          key: 'claim',
          component: 'PublicPortfolioClaim',
          isVisible: this.isClaimPlateVisible as boolean,
          canShowOnHeader: false,
        },
        {
          key: 'clients',
          title: 'Clients',
          component: 'PublicPortfolioClientsSection',
          isVisible: this.isClientsSectionVisible as boolean,
          canShowOnHeader: false,
          props: {
            configuration: this.configuration,
          },
        },
        {
          key: 'testimonials',
          title: 'Testimonials',
          component: 'PublicPortfolioTestimonialsSection',
          isVisible: this.configuration.sectionTestimonialsIsPublic && this.hasVisibleTestimonials,
          canShowOnHeader: true,
          withSeparator: this.isTestimonialsSeparatorVisible,
          props: {
            testimonials: this.testimonials,
            configuration: this.configuration,
          },
        },
        {
          key: 'awards',
          title: 'Awards',
          component: 'PublicPortfolioAwardsSection',
          isVisible: this.configuration.sectionAwardsIsPublic && this.hasAwards,
          canShowOnHeader: true,
          withSeparator: true,
          props: {
            configuration: this.configuration,
          },
        },
        {
          key: 'sales-experience',
          title: 'Sales Experience',
          component: 'PublicPortfolioSalesExperience',
          isVisible: this.configuration.sectionSalesExperienceIsPublic && this.hasSalesExperience,
          props: {
            user: this.publicPortfolioUser,
            configuration: this.configuration,
          },
          canShowOnHeader: false,
          withSeparator: true,
        },
        {
          key: 'work-history',
          title: 'Work History',
          component: 'PublicPortfolioWorkHistorySection',
          isVisible: this.configuration.sectionWorkHistoriesIsPublic && this.hasWorkHistories,
          canShowOnHeader: false,
          props: {
            configuration: this.configuration,
          },
        },
        {
          key: 'education-history',
          title: 'Education',
          component: 'PublicPortfolioEducationHistorySection',
          isVisible:
            this.configuration.sectionEducationHistoriesIsPublic &&
            Boolean(this.publicPortfolioUser.educationHistories?.length),
          canShowOnHeader: false,
          props: {
            user: this.publicPortfolioUser,
          },
        },
        {
          key: 'public-posts',
          title: 'Contributions to the sales community',
          component: 'PublicPortfolioPostsSection',
          isVisible: Boolean(this.publicPosts?.nodes?.length),
          canShowOnHeader: false,
          props: {
            posts: this.publicPosts,
          },
        },
      ];
    },
    visibleSections(): Array<PortfolioSection> {
      return (this.portfolioSections as PortfolioSection[]).filter(s => s.isVisible && s.key !== 'clients');
    },
    headerSection(): PortfolioSection | null {
      const hasHeaderSection = !this.isPortfolioEmpty && !this.isClientsSectionVisible && !this.$device.isMobile;

      const firstVisibleSection = (this.visibleSections as PortfolioSection[]).find(s => s.canShowOnHeader) || null;
      return hasHeaderSection ? firstVisibleSection : null;
    },
    bodySections(): Array<PortfolioSection> {
      return (this.visibleSections as PortfolioSection[]).filter(
        s => s.key !== (this.headerSection as PortfolioSection | null)?.key,
      );
    },
    rootClass(): Record<string, boolean> {
      return {
        'public-portfolio-preview': true,
        'public-portfolio-preview__self-preview': this.selfPreview,
      };
    },
    shouldHideMenu() {
      if (!this.$auth.loggedIn) return true;
      if (this.selfPreview) return true;
      return false;
    },
  },
});
